import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";

/** POST body for backend `pharmacy/appeal/update`. */
export interface UpdateAppealPayload {
  identifier: string;
  appeal_status?: string;
  appeal_type?: string;
  appeal_poc?: string | null;
  appeal_comment?: string | null;
  appeal_reason?: string | null;
  /** Payer-side contact the appeal was raised with. */
  appeal_insurance_poc?: string | null;
  /** Payer's reference number for the appeal. */
  appeal_case_id?: string | null;
  /** Phone or fax number the payer was reached on. */
  appeal_reachout_number?: string | null;
  /** Follow-up owed on the appeal. */
  appeal_next_action?: string | null;
  /** Date the follow-up is due, as YYYY-MM-DD. */
  appeal_follow_up_date?: string | null;
  /** Storage path of the faxed appeal document. */
  appeal_document_path?: string | null;
  /** Original file name of the faxed appeal document. */
  appeal_document_name?: string | null;
}

/** Parsed response from backend `pharmacy/appeal/update`. */
export interface UpdateAppealResponse {
  message?: string;
  success?: boolean;
  identifier?: string;
}

/**
 * Updates appeal fields for a case on the Appeal tab.
 *
 * @param payload - Order identifier and appeal fields to update
 * @returns Parsed JSON response from backend service
 * @throws {Error} When auth is missing or the API returns a non-OK status
 */
export const updateAppeal = async (
  payload: UpdateAppealPayload,
): Promise<UpdateAppealResponse> => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const body: Record<string, unknown> = {
      identifier: payload.identifier,
    };

    if (payload.appeal_status !== undefined) {
      body.appeal_status = payload.appeal_status;
    }
    if (payload.appeal_type !== undefined) {
      body.appeal_type = payload.appeal_type;
    }
    if (payload.appeal_poc !== undefined) {
      body.appeal_poc = payload.appeal_poc;
    }
    if (payload.appeal_comment !== undefined) {
      body.appeal_comment = payload.appeal_comment;
    }
    if (payload.appeal_reason !== undefined) {
      body.appeal_reason = payload.appeal_reason;
    }
    if (payload.appeal_insurance_poc !== undefined) {
      body.appeal_insurance_poc = payload.appeal_insurance_poc;
    }
    if (payload.appeal_case_id !== undefined) {
      body.appeal_case_id = payload.appeal_case_id;
    }
    if (payload.appeal_reachout_number !== undefined) {
      body.appeal_reachout_number = payload.appeal_reachout_number;
    }
    if (payload.appeal_next_action !== undefined) {
      body.appeal_next_action = payload.appeal_next_action;
    }
    if (payload.appeal_follow_up_date !== undefined) {
      body.appeal_follow_up_date = payload.appeal_follow_up_date;
    }
    if (payload.appeal_document_path !== undefined) {
      body.appeal_document_path = payload.appeal_document_path;
    }
    if (payload.appeal_document_name !== undefined) {
      body.appeal_document_name = payload.appeal_document_name;
    }

    const response = await fetch(API_ENDPOINTS.APPEAL_UPDATE, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    const responseText = await response.text();
    let parsed: UpdateAppealResponse = {};

    if (responseText.trim()) {
      try {
        parsed = JSON.parse(responseText) as UpdateAppealResponse;
      } catch {
        parsed = { message: responseText };
      }
    }

    if (!response.ok) {
      throw new Error(
        parsed.message ||
          `Failed to update appeal: ${response.status} - ${responseText}`,
      );
    }

    return parsed;
  } catch (error) {
    logError(error as Error, "updateAppeal");
    throw error;
  }
};
